import type { CustomLinkProps } from '@anchorage/utils';
import { Text } from '@latitude/text';
import { Tooltip } from '@latitude/tooltip';

import { CUSTOM_LINK_URLS_BY_HREF_ID } from '../../utils/customLinkUrls';
import { CTCTooltip } from '../CTCTooltip';

/**
 * Renders the `<customlink id="...">` markup that `parseSanitize` finds in
 * question text (titles, subtitles, headers, footers, page and group headers).
 * Shared by the onboarding form and the backoffice review so a new id only has
 * to be added once.
 */
export const CustomLink = ({ hrefId, children }: CustomLinkProps) => {
  // Own-property check: the map is a plain object, so an id such as
  // "constructor" must not resolve to an inherited Object member.
  const url = Object.hasOwn(CUSTOM_LINK_URLS_BY_HREF_ID, hrefId)
    ? CUSTOM_LINK_URLS_BY_HREF_ID[hrefId]
    : undefined;
  if (url) {
    return (
      <a
        className="text-color-ref-text-link-primary"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  switch (hrefId) {
    case 'faq-ctc':
      return <CTCTooltip>{children}</CTCTooltip>;
    case 'faq-authorized-user':
      return (
        <div className="w-fit">
          <Tooltip
            className="text-clip"
            text={
              <div className="flex flex-col gap-2">
                <div className="ml-3.5 text-left">
                  <Text variant="captionMedium">
                    An authorized user is an individual authorized by the client
                    to perform transactions and participate in quorum actions
                    using the Anchorage Digital app.
                    <br />
                    <br />
                    Authorized users may perform sensitive operations, such as:
                    <br />
                    <br />
                    <ul className="mx-3 list-disc">
                      <li>Creating vaults</li>
                      <li>Withdrawing assets</li>
                      <li>Interacting with dApps and smart contracts</li>
                      <li>Adding or removing users</li>
                      <li>Allowlisting addresses</li>
                      <li>Decrypting wallet keys</li>
                    </ul>
                  </Text>
                </div>
              </div>
            }
          >
            <Text data-testid="tooltip-text" color="link">
              {children}
            </Text>
          </Tooltip>
        </div>
      );
    default:
      return <>{children}</>;
  }
};
